import type { Hunt, RsvpStatus } from '@wehunt/ui'
import type { UpcomingHuntDto } from '../overview/useUpcomingHunts'

function pad2(n: number): string {
  return n.toString().padStart(2, '0')
}

export function rsvpFromInviteStatus(s: string | null | undefined): RsvpStatus | undefined {
  switch (s) {
    case 'pending':
      return 'pending'
    case 'accepted':
      return 'accepted'
    case 'rejected':
      return 'declined'
    default:
      return undefined
  }
}

/**
 * Whether the upcoming-feed status string marks a hunt as currently in
 * progress. The backend lifecycle trigger isn't finalized yet (see CLUB
 * ongoing story open questions), so we match the likely spellings
 * defensively — an unmatched value just stays "upcoming" (no regression),
 * and the web lights up the moment the backend starts emitting one of these.
 */
/**
 * An invitation the user never answered, for a hunt whose start date has passed.
 * The API reports this status; no document stores it. Checked BEFORE the ongoing
 * test, because an expired entry must never be presented as a live hunt.
 */
function isExpiredStatus(status: string | null | undefined): boolean {
  return status?.trim().toLowerCase() === 'expired'
}

function isOngoingStatus(status: string | null | undefined): boolean {
  const s = status?.trim().toLowerCase()
  return s === 'ongoing' || s === 'started' || s === 'inprogress' || s === 'live'
}

export function upcomingDtoToHunt(dto: UpcomingHuntDto): Hunt {
  const start = new Date(dto.startTime)
  const end = dto.endTime ? new Date(dto.endTime) : start
  const quarry = dto.huntableGames.length > 0 ? dto.huntableGames.join(', ') : '—'
  return {
    id: dto.id,
    date: `${start.getFullYear()}-${pad2(start.getMonth() + 1)}-${pad2(start.getDate())}`,
    endDate: dto.endTime
      ? `${end.getFullYear()}-${pad2(end.getMonth() + 1)}-${pad2(end.getDate())}`
      : undefined,
    start: `${pad2(start.getHours())}:${pad2(start.getMinutes())}`,
    end: `${pad2(end.getHours())}:${pad2(end.getMinutes())}`,
    team: dto.groupName,
    teamId: dto.groupId,
    quarry,
    location: dto.huntName,
    participants: dto.participantCount,
    dogs: [],
    status: isExpiredStatus(dto.status)
      ? 'expired'
      : isOngoingStatus(dto.status)
        ? 'ongoing'
        : 'upcoming',
    invitedBy: dto.invitedBy ?? undefined,
    invitedAt: dto.invitedAt ?? undefined,
    myRsvp: rsvpFromInviteStatus(dto.inviteStatus),
  }
}
